import { useState } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';


import '../../styles/SecuritySettings.css';
import { showMessage } from '../../showMessage';

const AUTH_ERROR_KEY = 'settings.security.authError' as const;
const API_BASE_URL: string = process.env.REACT_APP_API_BASE_URL ?? '';
const BACKEND_URL_MISSING_KEY = 'settings.security.backendUrlMissing' as const;

type ApiMessage = { message?: string };

function validatePassword(pwd: string, t: (k: string) => string): { ok: boolean; message: string } {
  if (pwd.length < 8) {
    return { ok: false, message: t('admin.passwordMinLength') };
  }
  if (!/[a-z]/.test(pwd)) {
    return { ok: false, message: t('settings.security.passwordLowercase') };
  }
  if (!/[A-Z]/.test(pwd)) {
    return { ok: false, message: t('settings.security.passwordUppercase') };
  }
  if (!/\d/.test(pwd)) {
    return { ok: false, message: t('settings.security.passwordNumber') };
  }
  if (!/[!"#$%&'()*+,./:;<=>?@[\\\]^_{|}-]/.test(pwd)) {
    return { ok: false, message: t('settings.security.passwordSpecial') };
  }
  return { ok: true, message: '' };
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data: unknown = error.response?.data;
    if (data && typeof data === 'object') {
      const maybeMessage = (data as Record<string, unknown>).message;
      if (typeof maybeMessage === 'string') {
        return maybeMessage;
      }
    }
  }
  return fallback;
}

const SecuritySettings = () => {
  const { t } = useTranslation('common');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [messages, setMessages] = useState<{ type: string; text: string }[]>([]);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handlePasswordChange = (event_: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event_.target;
    setPasswordForm((previous) => ({ ...previous, [name]: value }));
  };

  const handlePasswordSubmit = async (event_: React.FormEvent) => {
    event_.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessages([{ type: 'error', text: t('settings.security.newPasswordsNoMatch') }]);
      return;
    }

    const strength = validatePassword(passwordForm.newPassword, t);
    if (!strength.ok) {
      setMessages([{ type: 'error', text: strength.message }]);
      return;
    }

    if (!API_BASE_URL) {
      setMessages([{ type: 'error', text: t(BACKEND_URL_MISSING_KEY) }]);
      return;
    }

    try {
      setIsChangingPassword(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setMessages([{ type: 'error', text: t(AUTH_ERROR_KEY) }]);
        return;
      }
      await axios.post<ApiMessage>(
        `${API_BASE_URL}/api/user/change-password`,
        {
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      showMessage('success', t('notifications.success'), t('settings.security.passwordChanged'));
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setMessages([{ type: 'success', text: t('settings.security.passwordChanged') }]);
    } catch (error: unknown) {
      setMessages([{ type: 'error', text: extractErrorMessage(error, t('settings.security.errorChangingPassword')) }]);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="settings-form-container-security">
      <h2 className="settings-form-title-security">{t('settings.security.title')}</h2>

      {messages.map((message, index) => (
        <div key={index} className={`message-${message.type}`}>
          {message.text}
        </div>
      ))}

      <form onSubmit={handlePasswordSubmit} className="password-form-security">
        <h3>{t('settings.security.changePassword')}</h3>
        <div className="form-group-security">
          <label htmlFor="currentPassword">{t('settings.security.currentPassword')}</label>
          <input
            type="password"
            id="currentPassword"
            name="currentPassword"
            value={passwordForm.currentPassword}
            onChange={handlePasswordChange}
            required
            autoComplete="current-password"
          />
        </div>

        <div className="form-group-security">
          <label htmlFor="newPassword">{t('settings.security.newPassword')}</label>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            value={passwordForm.newPassword}
            onChange={handlePasswordChange}
            required
            minLength={8}
            autoComplete="new-password"
          />
          <p className="form-hint-security">{t('settings.security.passwordHint')}</p>
        </div>

        <div className="form-group-security">
          <label htmlFor="confirmPassword">{t('settings.security.confirmNewPassword')}</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={passwordForm.confirmPassword}
            onChange={handlePasswordChange}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>

        <button type="submit" className="save-btn-security" disabled={isChangingPassword}>
          {isChangingPassword ? t('settings.security.changing') : t('settings.security.change')}
        </button>
      </form>
    </div>
  );
};

export { SecuritySettings };
